'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { Download, RefreshCw, DollarSign, Clock, Users, TrendingUp } from 'lucide-react';
import { reportsService, type ReportExport } from '@/lib/services/staffingService';

import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import Icon from '@/components/ui/AppIcon';


interface BillableRow {
  staffName: string;
  role: string;
  facility: string;
  shiftDate: string;
  regularHours: number;
  otHours: number;
  approvalStatus: string;
}

interface RoleGroup {
  role: string;
  regularHours: number;
  otHours: number;
  totalHours: number;
  staffCount: number;
}

const RATE_BY_ROLE: Record<string, number> = {
  md: 145, np: 95, rn: 67, lpn: 42, cna: 28,
};

const PERIOD_OPTIONS = [
  { label: 'Last 30 days', start: offsetDate(-30), end: offsetDate(0) },
  { label: 'Last 60 days', start: offsetDate(-60), end: offsetDate(0) },
  { label: 'Last 90 days', start: offsetDate(-90), end: offsetDate(0) },
];

function offsetDate(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
}

function fmt$(n: number) {
  return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

interface Props {
  onExportLogged: (entry: ReportExport) => void;
}

export default function BillableHoursReport({ onExportLogged }: Props) {
  const [rows, setRows] = useState<BillableRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [periodIdx, setPeriodIdx] = useState(0);
  const [exporting, setExporting] = useState(false);

  const period = PERIOD_OPTIONS[periodIdx];

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await reportsService.getTimesheetSummary(period.start, period.end);
      setRows(data as BillableRow[]);
    } catch (e: unknown) {
      const err = e as { message?: string };
      setError(err?.message || 'Failed to load billable hours data');
    } finally {
      setLoading(false);
    }
  }, [period.start, period.end]);

  useEffect(() => { load(); }, [load]);

  // Aggregate by role for chart
  const roleGroups: RoleGroup[] = Object.values(
    rows.reduce<Record<string, RoleGroup>>((acc, r) => {
      const key = r.role.toLowerCase();
      if (!acc[key]) acc[key] = { role: r.role.toUpperCase(), regularHours: 0, otHours: 0, totalHours: 0, staffCount: 0 };
      acc[key].regularHours += r.regularHours;
      acc[key].otHours += r.otHours;
      acc[key].totalHours += r.regularHours + r.otHours;
      acc[key].staffCount += 1;
      return acc;
    }, {})
  );

  const totalRegular = rows.reduce((s, r) => s + r.regularHours, 0);
  const totalOT = rows.reduce((s, r) => s + r.otHours, 0);
  const totalHours = totalRegular + totalOT;
  const totalBillable = rows.reduce((s, r) => {
    const rate = RATE_BY_ROLE[r.role.toLowerCase()] || 45;
    return s + r.regularHours * rate + r.otHours * rate * 1.5;
  }, 0);
  const approvedRows = rows.filter((r) => r.approvalStatus === 'approved');
  const approvedHours = approvedRows.reduce((s, r) => s + r.regularHours + r.otHours, 0);

  const handleExport = async () => {
    setExporting(true);
    try {
      const csv = [
        ['Staff Name', 'Role', 'Facility', 'Shift Date', 'Regular Hrs', 'OT Hrs', 'Total Hrs', 'Est. Billable', 'Approval Status'].join(','),
        ...rows.map((r) => {
          const rate = RATE_BY_ROLE[r.role.toLowerCase()] || 45;
          const billable = r.regularHours * rate + r.otHours * rate * 1.5;
          return [
            `"${r.staffName}"`, r.role.toUpperCase(), `"${r.facility}"`, r.shiftDate,
            r.regularHours.toFixed(1), r.otHours.toFixed(1),
            (r.regularHours + r.otHours).toFixed(1),
            billable.toFixed(2), r.approvalStatus,
          ].join(',');
        }),
      ].join('\n');

      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const fileName = `billable_hours_${period.start}_${period.end}.csv`;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(url);

      const logged = await reportsService.logExport({
        reportType: 'billable_hours',
        title: `Billable Hours — ${period.label}`,
        generatedBy: null,
        generatedByName: 'Current User',
        periodStart: period.start,
        periodEnd: period.end,
        filters: { period: period.label },
        rowCount: rows.length,
        fileName,
        status: 'completed',
        notes: '',
      });
      if (logged) onExportLogged(logged);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-5 pt-5">
      {/* Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {PERIOD_OPTIONS.map((p, i) => (
            <button
              key={p.label}
              onClick={() => setPeriodIdx(i)}
              className={`px-3 py-1.5 text-xs font-500 rounded-lg border transition-colors ${
                periodIdx === i
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'border-border text-muted-foreground hover:text-foreground hover:border-foreground/30'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-500 border border-border rounded-lg text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
          >
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
          <button
            onClick={handleExport}
            disabled={exporting || rows.length === 0}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-600 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            <Download size={13} />
            {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Hours', value: totalHours.toFixed(0) + ' hrs', sub: `${totalRegular.toFixed(0)} reg + ${totalOT.toFixed(0)} OT`, icon: Clock, color: 'text-primary' },
          { label: 'Est. Billable Revenue', value: fmt$(totalBillable), sub: `${rows.length} timesheet entries`, icon: DollarSign, color: 'text-success' },
          { label: 'Approved Hours', value: approvedHours.toFixed(0) + ' hrs', sub: `${approvedRows.length} approved entries`, icon: TrendingUp, color: 'text-warning' },
          { label: 'Staff Covered', value: new Set(rows.map((r) => r.staffName)).size.toString(), sub: `Across ${new Set(rows.map((r) => r.facility)).size} facilities`, icon: Users, color: 'text-info' },
        ].map((card) => {
          const Icon = card.icon;
          return (
            <div key={card.label} className="card-elevated p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs text-muted-foreground font-500">{card.label}</p>
                <Icon size={16} className={card.color} />
              </div>
              <p className="text-xl font-700 text-foreground">{card.value}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{card.sub}</p>
            </div>
          );
        })}
      </div>

      {error && (
        <div className="bg-danger/10 border border-danger/20 rounded-lg px-4 py-3 text-sm text-danger">{error}</div>
      )}

      {/* Chart */}
      <div className="card-elevated p-5">
        <h3 className="text-sm font-600 text-foreground mb-4">Hours by Role — Regular vs Overtime</h3>
        {loading ? (
          <div className="h-52 flex items-center justify-center">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : roleGroups.length === 0 ? (
          <div className="h-52 flex items-center justify-center text-sm text-muted-foreground">No data for this period</div>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={roleGroups} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="role" tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} />
              <YAxis tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} />
              <Tooltip
                contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }}
                labelStyle={{ color: 'var(--foreground)', fontWeight: 600 }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="regularHours" name="Regular Hrs" fill="var(--primary)" radius={[3, 3, 0, 0]} />
              <Bar dataKey="otHours" name="OT Hrs" fill="var(--warning)" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Table */}
      <div className="card-elevated overflow-hidden">
        <div className="px-5 py-3 border-b border-border flex items-center justify-between">
          <h3 className="text-sm font-600 text-foreground">Timesheet Detail</h3>
          <span className="text-xs text-muted-foreground">{rows.length} entries</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                {['Staff Name', 'Role', 'Facility', 'Shift Date', 'Reg Hrs', 'OT Hrs', 'Est. Billable', 'Status'].map((h) => (
                  <th key={h} className="text-left px-4 py-2.5 text-xs font-600 text-muted-foreground whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="border-b border-border/50">
                    {Array.from({ length: 8 }).map((_, j) => (
                      <td key={j} className="px-4 py-3"><div className="h-3 bg-muted rounded animate-pulse w-20" /></td>
                    ))}
                  </tr>
                ))
              ) : rows.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-sm text-muted-foreground">No timesheet data for this period</td></tr>
              ) : (
                rows.map((r, i) => {
                  const rate = RATE_BY_ROLE[r.role.toLowerCase()] || 45;
                  const billable = r.regularHours * rate + r.otHours * rate * 1.5;
                  return (
                    <tr key={i} className="border-b border-border/50 hover:bg-muted/20 transition-colors">
                      <td className="px-4 py-2.5 font-500 text-foreground">{r.staffName}</td>
                      <td className="px-4 py-2.5 text-muted-foreground">{r.role.toUpperCase()}</td>
                      <td className="px-4 py-2.5 text-muted-foreground">{r.facility}</td>
                      <td className="px-4 py-2.5 text-muted-foreground">{r.shiftDate}</td>
                      <td className="px-4 py-2.5 text-foreground">{r.regularHours.toFixed(1)}</td>
                      <td className="px-4 py-2.5 text-warning font-500">{r.otHours > 0 ? r.otHours.toFixed(1) : '—'}</td>
                      <td className="px-4 py-2.5 text-success font-600">{fmt$(billable)}</td>
                      <td className="px-4 py-2.5">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-600 ${
                          r.approvalStatus === 'approved' ? 'bg-success/10 text-success' :
                          r.approvalStatus === 'pending'? 'bg-warning/10 text-warning' : 'bg-danger/10 text-danger'
                        }`}>
                          {r.approvalStatus}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
